import { DialogComponent } from '@theme/dialog';

/** How often to check again while something else is open (ms). */
const BUSY_RETRY_DELAY = 1000;

/** How long to wait after the page frees up before opening, so the pop-up doesn't chase a closing drawer (ms). */
const SETTLE_DELAY = 2000;

/** Set for good once the visitor signs up, so they never see the pop-up or its tag again. */
const SUBSCRIBED_KEY = 'welcome-popup-subscribed';

/**
 * Whether Shopify's cookie banner (or its preferences panel) is on screen waiting for an answer.
 * The modal pop-up would cover it and make it unusable.
 *
 * @returns {boolean}
 */
function isCookieBannerOpen() {
  return [...document.querySelectorAll('#shopify-pc__banner, #shopify-pc__prefs')].some(
    (element) => element.getClientRects().length > 0
  );
}

class WelcomePopup extends DialogComponent {
  #timer = null;
  #preloadTimer = null;
  #tagTimer = null;
  #stickyBarObserver = null;
  #copyTimer = null;
  #imagesLoaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (localStorage.getItem(SUBSCRIBED_KEY)) return;

    this.addEventListener('dialog:open', this.#handleOpen);
    this.addEventListener('dialog:close', this.#handleClose);
    window.addEventListener('klaviyoForms', this.#handleKlaviyoForms);

    if (this.refs.tag && document.querySelector('sticky-add-to-cart')) {
      this.#stickyBarObserver = new MutationObserver(this.#updateTagLift);
      this.#stickyBarObserver.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-stuck', 'data-variant-available'],
      });
      window.addEventListener('resize', this.#updateTagLift);
    }

    if (this.#wasDismissed()) {
      if (this.#shouldShowTag()) {
        const delay = (parseInt(this.dataset.tagDelay, 10) || 15) * 1000;
        this.#tagTimer = setTimeout(() => this.#showTag(), delay);
      }
      return;
    }

    const delay = (parseInt(this.dataset.delay, 10) || 15) * 1000;
    const preloadAt = Math.max(delay - 5000, 0);
    this.#preloadTimer = setTimeout(() => this.#activateImages(), preloadAt);
    this.#timer = setTimeout(() => this.#openWhenIdle(), delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('dialog:close', this.#handleClose);
    window.removeEventListener('klaviyoForms', this.#handleKlaviyoForms);
    if (this.#timer) clearTimeout(this.#timer);
    if (this.#preloadTimer) clearTimeout(this.#preloadTimer);
    if (this.#tagTimer) clearTimeout(this.#tagTimer);
    if (this.#stickyBarObserver) this.#stickyBarObserver.disconnect();
    window.removeEventListener('resize', this.#updateTagLift);
  }

  showDialog() {
    this.#activateImages();
    super.showDialog();
  }

  /**
   * Opens the pop-up once nothing else is open (cart drawer, menu, search or any other dialog, or the
   * cookie banner), checking again every second until then.
   *
   * @param {boolean} [waited] - Whether something was open on the previous check.
   */
  #openWhenIdle(waited = false) {
    if (this.#isPageBusy()) {
      this.#timer = setTimeout(() => this.#openWhenIdle(true), BUSY_RETRY_DELAY);
    } else if (waited) {
      this.#timer = setTimeout(() => this.#openWhenIdle(), SETTLE_DELAY);
    } else {
      this.showDialog();
    }
  }

  /**
   * @returns {boolean} Whether another dialog or drawer is open (or locks the page), or the cookie banner is waiting.
   */
  #isPageBusy() {
    const otherDialogOpen = [...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== this.refs.dialog);

    return otherDialogOpen || document.documentElement.hasAttribute('scroll-lock') || isCookieBannerOpen();
  }

  /**
   * Starts focus on the dialog itself rather than its first button (the close button), which some
   * browsers would draw a focus ring around even when the pop-up opened on its own.
   */
  #handleOpen = () => {
    this.refs.dialog.focus({ preventScroll: true });
  };

  #handleClose = () => {
    localStorage.setItem('welcome-popup-dismissed', Date.now().toString());

    if (localStorage.getItem(SUBSCRIBED_KEY)) return;

    localStorage.removeItem('welcome-popup-tag-dismissed');
    this.#showTag();
  };

  /**
   * Klaviyo's embedded form announces a successful sign-up with a `submit` event (it sends none when the
   * sign-up fails), so remember the sign-up for good and show the discount code.
   *
   * @param {CustomEvent} event - The klaviyoForms event.
   */
  #handleKlaviyoForms = (event) => {
    const { type, formId } = event.detail ?? {};
    if (formId !== this.dataset.klaviyoFormId) return;

    const { dialog, step2 } = this.refs;

    if (type === 'embedOpen' && step2 && !step2.inert && document.activeElement === dialog) {
      // The form loaded after step 2 appeared: move focus from the dialog to the email field.
      this.#focusStep(step2);
    } else if (type === 'submit') {
      localStorage.setItem(SUBSCRIBED_KEY, Date.now().toString());
      this.#hideTag();
      this.#transitionTo('step2', 'step3');
    }
  };

  goToStep2 = () => {
    this.#transitionTo('step1', 'step2');
  };

  openFromTag = () => {
    this.#hideTag();
    const { step1, step2, step3 } = this.refs;
    for (const step of [step2, step3]) {
      if (!step) continue;
      step.classList.remove('welcome-popup__step--active', 'welcome-popup__step--exit');
      step.inert = true;
    }
    if (step1) {
      step1.classList.remove('welcome-popup__step--exit');
      step1.classList.add('welcome-popup__step--active');
      step1.inert = false;
    }
    this.showDialog();
  };

  dismissTag = () => {
    localStorage.setItem('welcome-popup-tag-dismissed', 'true');
    this.#hideTag();
  };

  #activateImages() {
    if (this.#imagesLoaded) return;
    this.#imagesLoaded = true;
    const dialog = this.refs.dialog;
    if (!dialog) return;
    dialog.querySelectorAll('source[data-srcset]').forEach(s => {
      s.srcset = s.dataset.srcset;
      s.removeAttribute('data-srcset');
    });
    dialog.querySelectorAll('img[data-src]').forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }

  /**
   * Copies the discount code and shows "Copied!" briefly. The code comes from `data-code`, never the
   * label, so tapping again during the feedback still copies the code and the label always comes back.
   */
  copyCode = async () => {
    const codeEl = this.refs.codeText;
    const code = codeEl?.dataset.code;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    codeEl.textContent = 'Copied!';
    clearTimeout(this.#copyTimer);
    this.#copyTimer = setTimeout(() => {
      codeEl.textContent = code;
    }, 1500);
  };

  #transitionTo(fromRef, toRef) {
    const from = this.refs[fromRef];
    const to = this.refs[toRef];
    if (!from || !to) return;

    from.classList.remove('welcome-popup__step--active');
    from.classList.add('welcome-popup__step--exit');
    from.inert = true;

    setTimeout(() => {
      to.classList.add('welcome-popup__step--active');
      to.inert = false;
      this.#focusStep(to);
    }, 150);
  }

  /**
   * Moves focus into a step that just appeared, so keyboard and screen reader users follow along:
   * its first control (the email field, the code), or the dialog itself while the form is loading.
   *
   * @param {HTMLElement} step - The step.
   */
  #focusStep(step) {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    const control = step.querySelector('input:not([type="hidden"]), button, a[href]');
    (control instanceof HTMLElement ? control : dialog).focus({ preventScroll: true });
  }

  #showTag() {
    const tag = this.refs.tag;
    if (!tag) return;
    this.#updateTagLift();
    requestAnimationFrame(() => tag.classList.add('welcome-popup__tag--visible'));
  }

  #hideTag() {
    const tag = this.refs.tag;
    if (!tag) return;
    tag.classList.remove('welcome-popup__tag--visible');
  }

  /**
   * Lifts the tag above the product page's sticky add-to-cart bar while the bar is showing and the two
   * would overlap, using the bar's real offset and height.
   */
  #updateTagLift = () => {
    const { tag } = this.refs;
    if (!tag) return;

    const bar = document.querySelector('.sticky-add-to-cart__bar');
    let lift = 0;

    if (bar instanceof HTMLElement && bar.dataset.stuck === 'true' && bar.getClientRects().length > 0) {
      const barRect = bar.getBoundingClientRect();
      const tagRect = tag.getBoundingClientRect();

      if (barRect.left < tagRect.right && barRect.right > tagRect.left) {
        lift = (parseFloat(getComputedStyle(bar).bottom) || 0) + bar.offsetHeight;
      }
    }

    tag.style.setProperty('--tag-lift', `${lift}px`);
  };

  #shouldShowTag() {
    if (!this.refs.tag) return false;
    return !localStorage.getItem('welcome-popup-tag-dismissed');
  }

  #wasDismissed() {
    const dismissed = localStorage.getItem('welcome-popup-dismissed');
    if (!dismissed) return false;
    const dismissHours = parseInt(this.dataset.dismissHours, 10) || 72;
    const hoursSince = (Date.now() - Number(dismissed)) / (1000 * 60 * 60);
    if (hoursSince >= dismissHours) {
      localStorage.removeItem('welcome-popup-dismissed');
      localStorage.removeItem('welcome-popup-tag-dismissed');
      return false;
    }
    return true;
  }
}

if (!customElements.get('welcome-popup')) customElements.define('welcome-popup', WelcomePopup);
